<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import conversationAPI from 'dashboard/api/conversations';
import contactAPI from 'dashboard/api/contacts';
import labelsAPI from 'dashboard/api/labels';
import { getRandomColor } from 'dashboard/helper/labelColor';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

// State
const isLoading = ref(false);
const selectedLabels = ref([]);
const searchQuery = ref('');
const showCreateForm = ref(false);
const newLabelTitle = ref('');
const newLabelDescription = ref('');
const newLabelColor = ref('#000');

// Computed
const accountLabels = computed(() => store.getters['labels/getLabels'] || []);

const conversationLabels = computed(() => {
  if (!props.conversation?.id) return [];
  return (
    store.getters['conversationLabels/getConversationLabels'](
      props.conversation.id
    ) || []
  );
});

const contactLabels = computed(() => {
  if (!props.contact?.id) return [];
  return (
    store.getters['contactLabels/getContactLabels'](props.contact.id) || []
  );
});

const filteredLabels = computed(() => {
  const labels = accountLabels.value || [];
  if (!searchQuery.value) return labels;

  const query = searchQuery.value.toLowerCase();
  return labels.filter(label => label?.title?.toLowerCase().includes(query));
});

const hasChanges = computed(() => {
  const currentLabels = new Set([
    ...conversationLabels.value,
    ...contactLabels.value,
  ]);
  const selected = new Set(selectedLabels.value);

  if (currentLabels.size !== selected.size) return true;

  for (const label of selected) {
    if (!currentLabels.has(label)) return true;
  }
  return false;
});

// Methods
const initializeSelectedLabels = () => {
  // Start with labels that are on either conversation or contact
  const allCurrentLabels = new Set([
    ...conversationLabels.value,
    ...contactLabels.value,
  ]);
  selectedLabels.value = Array.from(allCurrentLabels);
};

const toggleLabel = labelTitle => {
  const index = selectedLabels.value.indexOf(labelTitle);
  if (index > -1) {
    selectedLabels.value.splice(index, 1);
  } else {
    selectedLabels.value.push(labelTitle);
  }
};

const isLabelSelected = labelTitle => {
  return selectedLabels.value.includes(labelTitle);
};

const createNewLabel = async () => {
  if (!newLabelTitle.value.trim()) {
    useAlert(t('LABEL_MGMT.FORM.NAME.REQUIRED'));
    return;
  }

  isLoading.value = true;
  try {
    const newLabel = await store.dispatch('labels/create', {
      title: newLabelTitle.value.toLowerCase(),
      description: newLabelDescription.value,
      color: newLabelColor.value,
      show_on_sidebar: false,
    });

    // Add the new label to selected labels
    selectedLabels.value.push(newLabel.title);

    // Reset form
    newLabelTitle.value = '';
    newLabelDescription.value = '';
    newLabelColor.value = getRandomColor();
    showCreateForm.value = false;

    useAlert(t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('LABEL_MGMT.ADD.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const applyLabels = async () => {
  isLoading.value = true;

  try {
    // Apply labels to conversation
    if (props.conversation?.id) {
      await conversationAPI.updateLabels(
        props.conversation.id,
        selectedLabels.value
      );
    }

    // Apply labels to contact
    if (props.contact?.id) {
      await contactAPI.updateContactLabels(
        props.contact.id,
        selectedLabels.value
      );
    }

    useAlert(t('CONTACT_PANEL.LABELS.UPDATE_SUCCESS'));
    emit('close');
  } catch (error) {
    useAlert(t('CONTACT_PANEL.LABELS.UPDATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const closeModal = () => {
  emit('close');
};

// Initialize
onMounted(() => {
  if (!props.show) return;

  initializeSelectedLabels();
  newLabelColor.value = getRandomColor();

  // Fetch labels if not loaded
  if (!accountLabels.value?.length) {
    store.dispatch('labels/get');
  }

  // Fetch conversation labels
  if (props.conversation?.id) {
    store.dispatch('conversationLabels/get', props.conversation.id);
  }

  // Fetch contact labels
  if (props.contact?.id) {
    store.dispatch('contactLabels/get', props.contact.id);
  }
});
</script>

<template>
  <woot-modal :show="show" @close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CONTACT_PANEL.ADD_LABEL')"
        :header-content="$t('CONTACT_PANEL.LABELS.MODAL_DESCRIPTION')"
      />

      <div class="flex flex-col p-6">
        <!-- Search Bar -->
        <div class="mb-4">
          <input
            v-model="searchQuery"
            type="text"
            class="w-full px-3 py-2 border border-n-neutral-200 rounded-lg"
            :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
          />
        </div>

        <!-- Labels List -->
        <div class="flex flex-col max-h-64 overflow-y-auto mb-4">
          <div v-if="isLoading" class="flex justify-center py-4">
            <Spinner />
          </div>

          <div
            v-else-if="!filteredLabels.length"
            class="text-center py-4 text-n-slate-600"
          >
            {{ $t('CONTACT_PANEL.LABELS.NO_LABELS_FOUND') }}
          </div>

          <div v-else class="space-y-2">
            <label
              v-for="label in filteredLabels"
              :key="label.id"
              class="flex items-center p-2 hover:bg-n-alpha-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                :checked="isLabelSelected(label.title)"
                class="mr-3"
                @change="toggleLabel(label.title)"
              />
              <span
                class="inline-block w-3 h-3 rounded mr-2"
                :style="{ backgroundColor: label.color }"
              />
              <span class="flex-1">{{ label.title }}</span>
            </label>
          </div>
        </div>

        <!-- Create New Label Section -->
        <div class="border-t pt-4">
          <button
            v-if="!showCreateForm"
            class="text-w-link text-sm font-medium"
            @click="showCreateForm = true"
          >
            + {{ $t('CONTACT_PANEL.LABELS.CREATE_NEW') }}
          </button>

          <div v-else class="space-y-3">
            <div class="flex items-center gap-2">
              <input
                v-model="newLabelColor"
                type="color"
                class="w-8 h-8 rounded cursor-pointer"
              />
              <input
                v-model="newLabelTitle"
                type="text"
                class="flex-1 px-3 py-2 border border-n-neutral-200 rounded-lg"
                :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
                @keyup.enter="createNewLabel"
              />
            </div>

            <textarea
              v-model="newLabelDescription"
              class="w-full px-3 py-2 border border-n-neutral-200 rounded-lg"
              rows="2"
              :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
            />

            <div class="flex gap-2">
              <NextButton
                size="sm"
                variant="solid"
                color="primary"
                :label="$t('LABEL_MGMT.FORM.CREATE')"
                :is-loading="isLoading"
                @click="createNewLabel"
              />
              <NextButton
                size="sm"
                variant="ghost"
                color="slate"
                :label="$t('LABEL_MGMT.FORM.CANCEL')"
                @click="showCreateForm = false"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-2 px-6 py-4 border-t">
        <NextButton
          variant="ghost"
          color="slate"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click="closeModal"
        />
        <NextButton
          variant="solid"
          color="primary"
          :label="$t('LABEL_MGMT.FORM.UPDATE')"
          :is-loading="isLoading"
          :disabled="!hasChanges && !selectedLabels.length"
          @click="applyLabels"
        />
      </div>
    </div>
  </woot-modal>
</template>
